import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import {
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import { walletService, TopUpRequest } from '../../../../services/WalletService'
import { billingService, PaymentMethod } from '../../../../services/BillingService'
import { StyledButton } from '../../styles/StyledComponents'

interface TopUpDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  companyId: string
  currentBalance: number
  disabled?: boolean
}

export const TopUpDialog: React.FC<TopUpDialogProps> = ({
  open,
  onClose,
  onSuccess,
  companyId,
  currentBalance,
  disabled = false,
}) => {
  const theme = useTheme()
  const [amount, setAmount] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethod[]>([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState(false)
  const [processing, setProcessing] = React.useState(false)

  // Load payment methods when dialog opens
  React.useEffect(() => {
    if (open) {
      loadPaymentMethods()
    }
  }, [open, companyId])

  // Listen for payment method updates from other components
  React.useEffect(() => {
    const handlePaymentMethodsUpdated = () => {
      console.log('Payment methods updated, refreshing TopUpDialog payment methods...')
      if (open) {
        loadPaymentMethods()
      }
    }

    window.addEventListener('paymentMethodsUpdated', handlePaymentMethodsUpdated)
    
    return () => {
      window.removeEventListener('paymentMethodsUpdated', handlePaymentMethodsUpdated)
    }
  }, [open, companyId])

  // Reset form when dialog closes
  React.useEffect(() => {
    if (!open) {
      setAmount('')
      setDescription('')
      setSelectedPaymentMethod('')
      setError(null)
      setSuccess(false)
      setProcessing(false)
    }
  }, [open])

  const loadPaymentMethods = async () => {
    try {
      const result = await billingService.getCompanyPaymentMethods(companyId)
      if (result.success && result.payment_methods) {
        // Only show active payment methods
        const activePaymentMethods = result.payment_methods.filter(pm => pm.status === 'active')
        setPaymentMethods(activePaymentMethods)
        // Auto-select default payment method
        const defaultMethod = activePaymentMethods.find(pm => pm.is_default)
        if (defaultMethod) {
          setSelectedPaymentMethod(defaultMethod.id)
        }
      }
    } catch (error: any) {
      console.error('Error loading payment methods:', error)
      setError('Failed to load payment methods')
    }
  }

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    // Allow only whole numbers (no decimals)
    if (value === '' || /^\d*$/.test(value)) {
      setAmount(value)
      setError(null)
    }
  }

  const handleSubmit = async () => {
    try {
      setError(null)
      setLoading(true)

      const amountNum = parseFloat(amount)
      
      // Validate amount
      const validation = walletService.validateTopUpAmount(amountNum)
      if (!validation.valid) {
        setError(validation.error || 'Invalid amount')
        return
      }

      // Check if payment method is selected
      if (!selectedPaymentMethod) {
        setError('Please select a payment method')
        return
      }

      // Check if wallet balance would exceed maximum
      const newBalance = currentBalance + amountNum
      if (newBalance > 50000) {
        setError('Wallet balance cannot exceed $50,000.00')
        return
      }

      const topUpRequest: TopUpRequest = {
        amount: amountNum,
        currency: 'usd',
        payment_method_id: selectedPaymentMethod,
        description: description || 'Wallet top-up'
      }

      // Create invoice-based top-up (uses wallet_top_up lookup product with quantity = dollars)
      const result = await walletService.initiateTopUpWithInvoice(companyId, topUpRequest)

      if (!result.success) {
        setError(result.error || 'Failed to initiate top-up invoice')
        return
      }

      // Success – invoice was created (and paid if payment method was provided)
      setSuccess(true)
      setTimeout(() => {
        onSuccess()
        onClose()
      }, 2000)

    } catch (error: any) {
      console.error('Error processing top-up:', error)
      setError(error.message || 'An unexpected error occurred')
    } finally {
      setLoading(false)
      setProcessing(false)
    }
  }

  const formatPaymentMethod = (paymentMethod: PaymentMethod): string => {
    if (paymentMethod.card) {
      return `${paymentMethod.card.brand.toUpperCase()} •••• ${paymentMethod.card.last4}`
    }
    if (paymentMethod.us_bank_account) {
      return `Bank Account •••• ${paymentMethod.us_bank_account.last4}`
    }
    return paymentMethod.type
  }

  const getPaymentMethodIcon = (paymentMethod: PaymentMethod) => {
    if (paymentMethod.card) {
      return <CreditCardIcon sx={{ fontSize: 16 }} />
    }
    return <AccountBalanceIcon sx={{ fontSize: 16 }} />
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: theme.shadows[10],
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'action.hover',
        backgroundImage: 'none',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AccountBalanceIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6">
            Top Up Wallet
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2, pt: 2.5 }}>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }}>
              Payment Successful!
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Your wallet has been topped up successfully.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Current Balance */}
            <Card
              sx={{
                mb: 3,
                mt: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                backgroundImage: 'none',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Current Balance
                </Typography>
                <Typography variant="h5" sx={{ color: 'info.main', fontWeight: 600 }}>
                  {walletService.formatCurrency(currentBalance)}
                </Typography>
              </CardContent>
            </Card>

            {/* Payment Method Warning */}
            {disabled && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 3,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  backgroundImage: 'none',
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
                  color: 'text.primary',
                  '& .MuiAlert-icon': {
                    color: 'warning.main'
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  <strong>No Payment Methods Available:</strong> Please add a payment method in the Billing & Tiers tab before topping up your wallet.
                </Typography>
              </Alert>
            )}

            {/* Amount Input */}
            <TextField
              fullWidth
              label="Amount"
              type="text"
              value={amount}
              onChange={handleAmountChange}
              placeholder="0"
              disabled={disabled}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: <Typography sx={{ color: 'text.secondary', mr: 1 }}>$</Typography>
              }}
              helperText="Minimum $5.00, Maximum $10,000.00 — whole dollars only"
            />

            {/* Payment Method Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={selectedPaymentMethod}
                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                label="Payment Method"
                disabled={disabled || paymentMethods.length === 0}
              >
                {paymentMethods.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                      No payment methods available
                    </Typography>
                  </MenuItem>
                ) : (
                  paymentMethods.map((method) => (
                    <MenuItem key={method.id} value={method.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getPaymentMethodIcon(method)}
                        <Typography variant="body2">
                          {formatPaymentMethod(method)}
                          {method.is_default && (
                            <Typography component="span" sx={{ color: 'success.main', ml: 1 }}>
                              (Default)
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Description */}
            <TextField
              fullWidth
              label="Description (Optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Monthly top-up"
              disabled={disabled}
              sx={{ mb: 3 }}
            />

            {/* Preview */}
            {amount && parseFloat(amount) > 0 && (
              <Card
                sx={{
                  bgcolor: 'action.hover',
                  backgroundImage: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                    Transaction Preview
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Current Balance:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {walletService.formatCurrency(currentBalance)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Top-up Amount:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'success.main' }}>
                      +{walletService.formatCurrency(parseFloat(amount) || 0)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1, borderColor: 'divider' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      New Balance:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 600 }}>
                      {walletService.formatCurrency(currentBalance + (parseFloat(amount) || 0))}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        bgcolor: 'action.hover',
        backgroundImage: 'none',
        borderTop: '1px solid',
        borderColor: 'divider',
        padding: '16px 24px'
      }}>
        <StyledButton 
          onClick={onClose}
          variant="outlined"
          disabled={loading || processing}
        >
          {success ? 'Close' : 'Cancel'}
        </StyledButton>
        {!success && (
          <StyledButton 
            onClick={handleSubmit}
            variant="contained"
            disabled={disabled || loading || processing || !amount || !selectedPaymentMethod}
            startIcon={loading || processing ? <CircularProgress size={16} sx={{ color: 'primary.contrastText' }} /> : <AccountBalanceIcon />}
          >
            {processing ? 'Processing...' : loading ? 'Creating...' : 'Top Up Wallet'}
          </StyledButton>
        )}
      </DialogActions>
    </Dialog>
  )
}
